import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useContent } from "@/lib/content";
import { API } from "@/lib/api";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { hero, headshot, testimonials } = useContent();
  const featured = (testimonials || []).find((t) => t.featured) || (testimonials || [])[0];
  const heroImage = headshot ? `${API}/files/headshots/${headshot}` : "/chloe-donan-headshot.png";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const featuredQuote = featured?.quote || null;

  return (
    <section id="top" ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse 60% 50% at 75% 30%, rgba(216,197,175,0.35), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 pt-24 lg:pt-28 pb-16 lg:pb-20 grid grid-cols-12 gap-4 sm:gap-6 lg:gap-12 items-start">
        <motion.div style={{ y: textY }} className="col-span-7 relative z-10">
          <h1
            className="font-serif font-medium text-burgundy tracking-tight leading-[0.92] text-[clamp(2.5rem,10vw,9rem)]"
            data-testid="hero-name"
          >
            <MaskedLine delay={0.45}>{hero.line1}</MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="italic font-light">{hero.line2}</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 lg:mt-8 max-w-md text-sm sm:text-base lg:text-lg leading-relaxed text-charcoal/80"
            data-testid="hero-tagline"
          >
            {hero.tagline}
          </motion.p>

          {featuredQuote && (
            <motion.figure
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block mt-6 lg:mt-8 max-w-lg border-l-2 border-burgundy pl-4 lg:pl-6"
              data-testid="hero-testimonial"
            >
              <blockquote className="font-serif italic text-sm sm:text-base lg:text-lg leading-snug text-burgundy/90">
                “{featuredQuote}”
              </blockquote>
              <figcaption className="mt-3 text-[10px] uppercase tracking-[0.25em] text-charcoal/60">
                {featured.name} <span className="text-charcoal/40">— {featured.role}</span>
              </figcaption>
            </motion.figure>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="hidden lg:flex mt-8 lg:mt-10 items-center gap-6"
          >
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="hero-cta-button"
              className="inline-flex items-center rounded-full bg-burgundy px-6 lg:px-8 py-3 lg:py-4 text-[10px] lg:text-[11px] font-semibold uppercase tracking-[0.25em] lg:tracking-[0.3em] text-ivory hover:bg-charcoal transition-colors duration-300"
            >
              {hero.cta}
            </a>
            <span className="hidden lg:block h-px w-24 bg-champagne" />
          </motion.div>
        </motion.div>

        <div className="col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div aria-hidden="true" className="hidden lg:block absolute -inset-4 border border-champagne translate-x-4 translate-y-4" />
            <motion.div style={{ y: imageY }} className="relative overflow-hidden">
              <img
                src={heroImage}
                alt="Chloe Donan, intimacy coordinator"
                data-testid="hero-image"
                className="w-full aspect-[3/4] object-cover object-top"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-burgundy/10 mix-blend-multiply" />
            </motion.div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-charcoal/50" data-testid="hero-caption">
              {hero.caption}
            </p>
          </motion.div>
        </div>

        {featuredQuote && (
          <motion.figure
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden col-span-12 mt-2 border-l-2 border-burgundy pl-4"
            data-testid="hero-testimonial-mobile"
          >
            <blockquote className="font-serif italic text-base leading-snug text-burgundy/90">
              “{featuredQuote}”
              <cite className="not-italic ml-2 whitespace-nowrap text-[10px] uppercase tracking-[0.25em] text-charcoal/60">
                — {featured.name}
              </cite>
            </blockquote>
          </motion.figure>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="lg:hidden col-span-12 mt-4"
        >
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
            data-testid="hero-cta-button-mobile"
            className="inline-flex items-center rounded-full bg-burgundy px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-ivory hover:bg-charcoal transition-colors duration-300"
          >
            {hero.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
